import type { NewsUpdate } from './types'
import type { BadgePack, PointsAwardEntry, BadgeAwardEntry, GamificationSettings } from '@/lib/gamification/types'

// ---------------------------------------------------------------------------
// Shared prop shapes used by PortalPreview
// ---------------------------------------------------------------------------

export interface NewsItemProp {
  category: string
  title: string
  description: string
  image: string
  content?: string
  publishedAt?: string
}

export interface TrainingSlideProp {
  title: string
  status: string
  dueDate: string
  image: string
  /**
   * Real progress percent (0-100) derived from enrollment.progress.
   * Single source of truth for the carousel card body + progress bar — eliminates
   * the bug class where badge=Completed but body="Not started" + 0% bar (ticket 696c6b46).
   * Optional so older consumers with hardcoded slide arrays don't TSC-fail.
   */
  progress?: number
  /**
   * FIX #682: enrollment primary key — enables the "Start Training →" button in
   * Important Updates to navigate to /portal/training/[enrollmentId].
   * Optional so hardcoded preview slide arrays in theme-builder don't TSC-fail.
   */
  enrollmentId?: string
}

export interface CourseCardProp {
  title: string
  image: string
  attempts: string
  status: string
  statusColor: string
  /**
   * Course due date (formatted via formatDate, e.g. "Dec 12, 2026") or empty
   * string when the enrollment has no scheduled deadline. Populated from
   * enrollment.dueDate. Used by portal-preview "Next deadline" meta to
   * sort incomplete trainings by upcoming deadline.
   * Optional so existing CourseCardProp consumers (e.g. theme previews
   * with hardcoded sample arrays) don't TSC-fail on the new field.
   */
  dueDate?: string
  /**
   * Real progress percent (0-100) derived from enrollment.progress.
   * Used by the trainings-tab card to branch the CTA copy
   * ("Start" / "Continue" / "Review") and show real progress instead of a
   * hardcoded "Start Training" for every status (ticket 9b36b6ae).
   */
  progress?: number
  /**
   * FIX #681: enrollment primary key — enables the "Start Training" button in
   * the Trainings tab to navigate to /portal/training/[enrollmentId].
   * Optional so hardcoded preview card arrays in theme-builder don't TSC-fail.
   */
  enrollmentId?: string
}

export interface PointsHistoryProp {
  activity: string
  points: string
  date: string
}

export interface BadgeCardProp {
  name: string
  icon: string
  earned: boolean
  description?: string
  earnedAt?: string
  relatedTraining?: string
  pointsEarned?: number
  eventCount?: number
}

export interface CertificateCardProp {
  name: string
  date: string
  status: string
}

export interface LevelDataProp {
  id: number
  name: string
  pointsRequired: number
  badge: string
}

// ---------------------------------------------------------------------------
// Enrollment / Course types (lightweight, avoids importing zod schemas)
// ---------------------------------------------------------------------------

interface EnrollmentLike {
  /** Enrollment row primary key — used to navigate to /portal/training/[enrollmentId] */
  id?: string
  learnerEmail: string
  courseId: string
  courseName: string
  status: string
  progress: number
  dueDate: string
  completionDate: string | null
}

interface CourseLike {
  id: string
  title: string
  thumbnail: string
  certificateEnabled?: boolean
}

// ---------------------------------------------------------------------------
// Transforms
// ---------------------------------------------------------------------------

export function transformNewsToProps(newsUpdates: NewsUpdate[]): NewsItemProp[] {
  const now = Date.now()
  return newsUpdates
    .filter((n) => {
      // FIX: D-D BUG#1 (ticket b0249912) — Important Updates carousel rendered 0 slides.
      // Root cause: filter used `!n.isActive` but the BE response carries `status` (Title
      // Case enum) not `isActive` (deprecated bool). All items had isActive=undefined,
      // making every item falsy and excluding all of them from the carousel.
      // Fix: prefer canonical `status === 'Published'` check; fall back to deprecated
      // `isActive` only for payloads that pre-date the status field.
      const isPublished = n.status
        ? n.status === 'Published'
        : Boolean(n.isActive)
      if (!isPublished) return false
      const start = new Date(n.publishStart).getTime()
      const end = new Date(n.publishEnd).getTime()
      // Show if current time is within the publish window (or if dates are invalid/missing)
      if (isNaN(start) || isNaN(end)) return true
      return now >= start && now <= end
    })
    .map((n) => ({
      category: 'News Updates',
      title: n.title,
      description: n.content,
      image:
        n.thumbnailUrl ||
        '',
      content: n.content,
      publishedAt: n.createdOn || n.publishStart,
    }))
}

// FIX (2026-05-14, ticket 9b36b6ae + 696c6b46): BE returns enrollment.status
// in Title Case ('Completed' | 'In Progress' | 'Not Started' | 'Overdue' —
// see lib/lms/schemas.ts:enrollmentStatusSchema). Previous keys were
// UPPER_SNAKE_CASE so EVERY enrollment fell through to the default branch:
// statusColor was always NOT_STARTED grey, label was passed through raw.
// Keys now match the canonical Title Case enum.
const STATUS_COLOR_MAP: Record<string, string> = {
  'In Progress':
    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  Completed:
    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  'Not Started': 'bg-muted/50 text-muted-foreground',
  Overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
}

function formatDate(dateStr: string): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function transformToTrainingSlides(
  enrollments: EnrollmentLike[],
  courses: CourseLike[],
  employeeEmail?: string,
): TrainingSlideProp[] {
  const courseMap = new Map(courses.map((c) => [c.id, c]))
  return enrollments
    .filter(
      (e) =>
        // FIX (2026-05-14, ticket 696c6b46): status comparison was
        // UPPER_SNAKE_CASE ('COMPLETED') against a Title Case BE enum
        // ('Completed'). Result: completed enrollments NEVER matched
        // the filter and leaked into the carousel — producing the
        // contradictory "badge=Completed + body=Not started + 0%" cards.
        (!employeeEmail || e.learnerEmail === employeeEmail) &&
        e.status !== 'Completed',
    )
    .slice(0, 5)
    .map((e) => {
      const course = courseMap.get(e.courseId)
      return {
        title: e.courseName,
        status: e.status,
        dueDate: formatDate(e.dueDate),
        image:
          course?.thumbnail ||
          '',
        progress: typeof e.progress === 'number' ? e.progress : 0,
        // FIX #682: surface enrollmentId so the carousel CTA can navigate to the player.
        enrollmentId: e.id,
      }
    })
}

export function transformToCourseCards(
  enrollments: EnrollmentLike[],
  courses: CourseLike[],
  employeeEmail?: string,
): CourseCardProp[] {
  const courseMap = new Map(courses.map((c) => [c.id, c]))
  // FIX (2026-05-14, ticket af89916d): used to cap at 6 (one page). The portal
  // preview's Trainings tab now paginates client-side via the "View More"
  // button — we need to surface every enrollment for that employee so the
  // pagination has data to reveal. Hard cap at 200 to defend against a
  // pathological 10k-row payload while still showing every realistic mailbox.
  return enrollments
    .filter((e) => !employeeEmail || e.learnerEmail === employeeEmail)
    .slice(0, 200)
    .map((e) => {
      const course = courseMap.get(e.courseId)
      return {
        title: e.courseName,
        image:
          course?.thumbnail ||
          '',
        // FIX (2026-05-14, ticket 9b36b6ae): same UPPER_SNAKE_CASE bug as the
        // training-slides transformer. attempts always read '0' because
        // 'COMPLETED' never matched the Title Case enum value.
        attempts: e.status === 'Completed' ? '1' : '0',
        status: e.status,
        statusColor: STATUS_COLOR_MAP[e.status] ?? STATUS_COLOR_MAP['Not Started'],
        // 2026-05-12 — surfaced for the portal-preview "Next deadline" meta.
        dueDate: formatDate(e.dueDate),
        progress: typeof e.progress === 'number' ? e.progress : 0,
        // FIX #681: surface enrollmentId so the Trainings-tab CTA can navigate to the player.
        enrollmentId: e.id,
      }
    })
}

function relativeDate(dateStr: string): string {
  try {
    const diff = Date.now() - new Date(dateStr).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 60) return `${mins} min ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`
    const days = Math.floor(hours / 24)
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`
    const weeks = Math.floor(days / 7)
    if (weeks < 5) return `${weeks} week${weeks > 1 ? 's' : ''} ago`
    const months = Math.floor(days / 30)
    return `${months} month${months > 1 ? 's' : ''} ago`
  } catch {
    return dateStr
  }
}

export function transformPointsHistory(
  awards: PointsAwardEntry[],
  employeeName?: string,
): PointsHistoryProp[] {
  return awards
    .filter((a) => !employeeName || a.employeeName === employeeName)
    .sort(
      (a, b) =>
        new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime(),
    )
    .slice(0, 10)
    .map((a) => ({
      activity: a.activity,
      points: `+${a.points}`,
      date: relativeDate(a.dateTime),
    }))
}

export function transformToBadgeCards(
  activePack: BadgePack | null,
  earnedBadgeIds: string[],
  badgeAwards: BadgeAwardEntry[] = [],
  pointsAwards: PointsAwardEntry[] = [],
  employeeEmail?: string,
): BadgeCardProp[] {
  if (!activePack) return []
  const userBadgeAwards = employeeEmail
    ? badgeAwards.filter((a) => a.employeeEmail === employeeEmail)
    : badgeAwards
  const userPointsAwards = employeeEmail
    ? pointsAwards.filter((a) => a.employeeEmail === employeeEmail)
    : pointsAwards
  // Achievement badges only (exclude level badges)
  return activePack.badges
    .filter((b) => !b.event.startsWith('level_'))
    .map((b) => {
      const award = userBadgeAwards.find((a) => a.badgeName === b.name)
      let relatedTraining: string | undefined
      let pointsEarned: number | undefined
      if (award) {
        const awardTime = new Date(award.dateTime).getTime()
        const nearby = userPointsAwards.find((p) => {
          const diff = Math.abs(new Date(p.dateTime).getTime() - awardTime)
          return diff <= 10 * 60 * 1000 && (p.trainingName || p.campaignName)
        })
        if (nearby) {
          relatedTraining = nearby.trainingName || nearby.campaignName
          pointsEarned = nearby.points
        }
      }
      // Ticket #246: the leaderboard `entries[].badges` array contains badge
      // *names* (strings), not UUIDs. Check both ID and name so the earned
      // flag is accurate regardless of which form the leaderboard returns.
      // Additionally treat a matching badgeAward row as definitive proof of
      // earning, since the activity-log is the primary source of truth.
      const earned =
        earnedBadgeIds.includes(b.id) ||
        earnedBadgeIds.includes(b.name) ||
        !!award;
      return {
        name: b.name,
        icon: b.icon,
        earned,
        description: b.eventDescription,
        earnedAt: award?.dateTime,
        eventCount: award?.eventCount,
        relatedTraining,
        pointsEarned,
      }
    })
}

export function transformToCertificateCards(
  enrollments: EnrollmentLike[],
  courses: CourseLike[],
  employeeEmail?: string,
): CertificateCardProp[] {
  const certCourseIds = new Set(
    courses.filter((c) => c.certificateEnabled).map((c) => c.id),
  )
  return enrollments
    .filter(
      (e) =>
        (!employeeEmail || e.learnerEmail === employeeEmail) &&
        certCourseIds.has(e.courseId),
    )
    .map((e) => ({
      name: `${e.courseName} Certificate`,
      date: e.completionDate ? formatDate(e.completionDate) : 'Pending',
      // FIX (2026-05-14): match Title Case BE enum, not UPPER_SNAKE_CASE.
      status: e.status === 'Completed' ? 'Completed' : 'In Progress',
    }))
}

export function findActiveBadgePack(
  packs: BadgePack[],
  settings: GamificationSettings | null,
): BadgePack | null {
  if (!packs.length) return null
  if (settings?.activeBadgePackId) {
    const found = packs.find((p) => p.id === settings.activeBadgePackId)
    if (found) return found
  }
  // Fallback: first default or first pack
  return packs.find((p) => p.isDefault) ?? packs[0]
}

const LEVEL_POINTS = [200, 400, 800, 1200, 1600]

export function transformLevels(
  activePack: BadgePack | null,
): LevelDataProp[] {
  if (!activePack) return []
  const levelBadges = activePack.badges
    .filter((b) => b.event.startsWith('level_'))
    .sort((a, b) => {
      const aNum = parseInt(a.event.replace('level_', ''), 10)
      const bNum = parseInt(b.event.replace('level_', ''), 10)
      return aNum - bNum
    })
  return levelBadges.map((b, i) => ({
    id: i + 1,
    name: b.name,
    pointsRequired: LEVEL_POINTS[i] ?? (i + 1) * 400,
    badge: b.icon,
  }))
}
